import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnimateOnView } from '../../hooks/useIntersectionObserver';
import './HowToBookSection.css';

const STEPS = [
  {
    number: '01',
    title: 'Elige tu terapia',
    description:
      'Tipo de terapia —individual o pareja—, terapeuta, duración y modalidad presencial u online.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="4" y1="6" x2="20" y2="6" />
        <circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
        <line x1="4" y1="12" x2="20" y2="12" />
        <circle cx="16" cy="12" r="2" fill="currentColor" stroke="none" />
        <line x1="4" y1="18" x2="20" y2="18" />
        <circle cx="12" cy="18" r="2" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Elige fecha y hora',
    description: 'Revisa la disponibilidad y selecciona el día y horario que más te acomode.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
        <line x1="3.5" y1="9.5" x2="20.5" y2="9.5" />
        <line x1="8" y1="3" x2="8" y2="6.5" />
        <line x1="16" y1="3" x2="16" y2="6.5" />
        <circle cx="8.5" cy="14" r="1" fill="currentColor" stroke="none" />
        <circle cx="12" cy="14" r="1" fill="currentColor" stroke="none" />
        <circle cx="15.5" cy="14" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Ingresa tus datos',
    description: 'Completa tu nombre, RUT y datos de contacto para confirmar la reserva.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
        <circle cx="9" cy="10" r="2" />
        <path d="M5.5 17c0.5-2.5 2.2-3.5 3.5-3.5s3 1 3.5 3.5" />
        <line x1="15" y1="9" x2="17.5" y2="9" />
        <line x1="15" y1="12" x2="17.5" y2="12" />
      </svg>
    ),
  },
];

const HowToBookSection: React.FC = () => {
  const navigate = useNavigate();
  const [ref, shouldAnimate] = useAnimateOnView();

  return (
    <section
      ref={ref}
      className={`how-to-book section-padding${shouldAnimate ? ' loaded' : ''}`}
      id="como-agendar"
    >
      <div className="container">
        <div className="section-header">
          <span className="htb-eyebrow animate-in" style={{ '--delay': '0s' } as React.CSSProperties}>
            Agenda tu hora
          </span>
          <h2 className="section-title htb-title">
            <span>Reserva tu sesión en 3 pasos</span>
          </h2>
          <p className="section-subtitle htb-subtitle">
            Un recorrido simple, pensado para acompañarte desde la elección de tu terapia hasta la
            confirmación de tu hora.
          </p>
        </div>

        <div className="htb-orbit animate-in" style={{ '--delay': '0.15s' } as React.CSSProperties}>
          <div className="htb-orbit__ring" aria-hidden="true"></div>

          <div className="htb-orbit__hub">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3.5 2" strokeLinecap="round" />
            </svg>
            <span>Menos de 5 min</span>
          </div>

          <svg className="htb-orbit__paths" viewBox="0 0 100 100" aria-hidden="true">
            {/* Rotación de cada flecha = tangente real del arco en ese punto
                (no una línea recta hacia el step — debe seguir la curva).
                Los 3 arcos son círculo radio 34 centrado en (50,50); para un
                punto a ángulo θ desde el centro moviéndose en sentido horario
                (flujo 01→02→03→01, confirmado por los sweep-flag=1 de los
                arcos), la tangente = θ+90°. Ver FUNC-049.
                arrow1 (76,52), θ=atan2(2,26)=4.4° → tangente 94°
                arrow2 (50,85), θ=atan2(35,0)=90° → tangente 180°
                arrow3 (24,52), θ=atan2(2,-26)=175.6° → tangente 266° */}
            <path className="htb-orbit__arc" d="M 50 16 A 34 34 0 0 1 79 68" fill="none" />
            <g transform="translate(76,52) rotate(94)">
              <path className="htb-orbit__arrow" d="M-4 -2 L0 0 L-4 2" fill="none" style={{ animationDelay: '0s' }} />
            </g>
            <path className="htb-orbit__arc" d="M 79 68 A 34 34 0 0 1 21 68" fill="none" />
            <g transform="translate(50,85) rotate(180)">
              <path className="htb-orbit__arrow" d="M-4 -2 L0 0 L-4 2" fill="none" style={{ animationDelay: '0.6s' }} />
            </g>
            <path className="htb-orbit__arc" d="M 21 68 A 34 34 0 0 1 50 16" fill="none" />
            <g transform="translate(24,52) rotate(266)">
              <path className="htb-orbit__arrow" d="M-4 -2 L0 0 L-4 2" fill="none" style={{ animationDelay: '1.2s' }} />
            </g>
          </svg>

          {STEPS.map((step, index) => (
            <div
              key={step.number}
              className={`htb-orbit__card htb-orbit__card--${index + 1} animate-in`}
              style={{ '--delay': `${0.25 + index * 0.15}s` } as React.CSSProperties}
            >
              <span className="htb-orbit__num">{step.number}</span>
              <div className="htb-orbit__icon">{step.icon}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>

        <div className="htb-confirm-wrap">
          <div className="htb-confirm-link" aria-hidden="true"></div>
          <div className="htb-confirm animate-in" style={{ '--delay': '0.7s' } as React.CSSProperties}>
            <div className="htb-confirm__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3.5 6l8.5 7 8.5-7" />
              </svg>
            </div>
            <div className="htb-confirm__text">
              <span className="htb-confirm__eyebrow">Paso final · automático</span>
              <span className="htb-confirm__title">Correo de confirmación</span>
              <span className="htb-confirm__desc">
                Recibirás todos los detalles de tu sesión apenas selecciones fecha y hora.
              </span>
            </div>
          </div>
        </div>

        <div className="htb-cta animate-in" style={{ '--delay': '0.85s' } as React.CSSProperties}>
          <button
            className="htb-cta__btn"
            onClick={() => navigate('/agendar')}
            aria-label="Ir al agendamiento online"
          >
            <i className="fas fa-calendar-plus" aria-hidden="true"></i>
            Agendar mi primera consulta
          </button>
          <a
            className="htb-cta__whatsapp"
            href="https://wa.me/56978009119?text=Hola,%20me%20gustar%C3%ADa%20agendar%20una%20consulta%20en%20Conectemos%20Chile"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Contactar por WhatsApp"
          >
            <i className="fab fa-whatsapp" aria-hidden="true"></i>
            ¿Tienes dudas antes de agendar? Escríbenos por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowToBookSection;
